import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Grid,
  GridItem,
  HStack,
  Heading,
  Icon,
  IconButton,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiFilter,
  FiSliders,
} from "react-icons/fi";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../../configs/firebase";
import { useCombinedStore } from "../../store/hooks/useCombinedStore";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import { addToCart } from "../../store/cartSlice/cartSlice";
import { fetchAllTrips } from "../../store/travelSlice/hikeTripsSlice";
import BusinessListingShowcaseCard from "./BusinessListingShowcaseCard";
import BusinessQrShareActions from "./BusinessQrShareActions";
import ListingFilterSidebar, {
  BusinessListingFilterOption,
  BusinessListingFilters,
} from "./ListingFilterSidebar";
import ReusableStyledTabs, {
  ReusableStyledTabItem,
} from "../Shared/ReusableStyledTabs";
import { normalizeCarImages } from "../Travel/Cars/dummyCarImages";
import { HikeSearchTrips } from "../Travel/Hikes/hikeSearchTrips";
import GlobalSpinner from "../CustomReusables/GlobalSpinner";

const ChevronLeftIcon = FiChevronLeft as unknown as React.ElementType;
const ChevronRightIcon = FiChevronRight as unknown as React.ElementType;
const FilterIcon = FiFilter as unknown as React.ElementType;
const SlidersIcon = FiSliders as unknown as React.ElementType;
const cardsBorderRadii = "8px";

interface ProfileData {
  id: string;
  name?: string;
  businessName?: string;
  businessAddress?: string;
  logoURL?: string;
  phoneNumber?: string;
  role?: string;
  bio?: string;
  description?: string;
  businessType?: string;
  city?: string;
  country?: string;
  businessCode?: string;
  businessQrGenerated?: boolean;
}

type ListingType = "products" | "accommodations" | "hikes" | "carHire";

type BusinessTab = {
  key: string;
  label: string;
  type: ListingType;
  items: any[];
};

type FiltersByBusinessTab = Record<string, BusinessListingFilters>;

const ITEMS_PER_PAGE = 30;

const API_BASE = (
  process.env.REACT_APP_BACKEND_URL || "http://localhost:5000"
).replace(/\/+$/, "");

const getFirstDisplayValue = (...values: any[]) => {
  for (const value of values) {
    const displayValue = valueToDisplayText(value).trim();

    if (displayValue) {
      return displayValue;
    }
  }

  return "";
};

const mapProfileData = (
  businessId: string,
  data: Record<string, any>,
): ProfileData => {
  const name = getFirstDisplayValue(data.name, data.displayName);
  const businessName = getFirstDisplayValue(
    data.businessName,
    data.companyName,
    data.storeName,
    name,
  );

  return {
    id: String(data.id || data.uid || businessId),
    name,
    businessName,
    businessAddress: getFirstDisplayValue(
      data.businessAddress,
      data.address,
      data.location,
      data.formattedAddress,
    ),
    logoURL: getFirstDisplayValue(
      data.logoURL,
      data.photoURL,
      data.businessLogo,
      data.imageUrl,
    ),
    phoneNumber: getFirstDisplayValue(
      data.businessPhoneNumber,
      data.businessPhone,
      data.whatsappNumber,
      data.phoneNumber,
      data.phone,
    ),
    role: getFirstDisplayValue(data.role, "business"),
    bio: getFirstDisplayValue(data.bio, data.tagline, data.about),
    description: getFirstDisplayValue(
      data.description,
      data.businessDescription,
      data.aboutBusiness,
    ),
    businessType: getFirstDisplayValue(
      data.businessType,
      data.category,
      data.industry,
    ),
    city: getFirstDisplayValue(data.city, data.town),
    country: getFirstDisplayValue(data.country),
    businessCode: getFirstDisplayValue(data.businessCode),
    businessQrGenerated:
      data.businessQrGenerated === true ||
      Boolean(getFirstDisplayValue(data.businessCode)),
  };
};

const DEFAULT_FILTERS: BusinessListingFilters = {
  search: "",
  categories: [],
  priceRange: [0, 0],
  minDiscount: 0,
  deliveryEligibleOnly: false,
  availableOnly: false,
  location: "",
  sortBy: "recommended",
};

const TRIP_PLACEHOLDER_IMAGE = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg width="800" height="520" viewBox="0 0 800 520" fill="none" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="800" y2="520" gradientUnits="userSpaceOnUse">
      <stop stop-color="#0F766E"/>
      <stop offset="1" stop-color="#2563EB"/>
    </linearGradient>
    <linearGradient id="card" x1="180" y1="130" x2="620" y2="390" gradientUnits="userSpaceOnUse">
      <stop stop-color="white" stop-opacity="0.24"/>
      <stop offset="1" stop-color="white" stop-opacity="0.08"/>
    </linearGradient>
  </defs>
  <rect width="800" height="520" rx="34" fill="url(#bg)"/>
  <circle cx="122" cy="110" r="70" fill="white" fill-opacity="0.08"/>
  <circle cx="706" cy="420" r="95" fill="white" fill-opacity="0.08"/>
  <rect x="160" y="122" width="480" height="276" rx="34" fill="url(#card)" stroke="white" stroke-opacity="0.22" stroke-width="2"/>
  <path d="M248 294C295 240 348 240 400 294C452 348 505 348 552 294" stroke="white" stroke-width="18" stroke-linecap="round"/>
  <circle cx="248" cy="294" r="24" fill="white"/>
  <circle cx="552" cy="294" r="24" fill="white"/>
  <path d="M333 206H467C492 206 513 227 513 252V294H287V252C287 227 308 206 333 206Z" fill="white" fill-opacity="0.92"/>
  <path d="M320 294H480" stroke="#0F766E" stroke-width="18" stroke-linecap="round"/>
  <circle cx="338" cy="324" r="22" fill="#0F172A"/>
  <circle cx="462" cy="324" r="22" fill="#0F172A"/>
  <text x="400" y="452" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="34" font-weight="700">Upcoming Trip</text>
</svg>
`)}`;

const getTodayStartTime = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return today.getTime();
};

const valueToDisplayText = (value: any): string => {
  if (!value) return "";

  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);

  if (typeof value === "object") {
    return (
      value.address ||
      value.name ||
      value.label ||
      value.formattedAddress ||
      value.description ||
      ""
    );
  }

  return String(value);
};

const parseDateTime = (rawDate: any, rawTime?: any) => {
  if (!rawDate) return 0;

  const dateText = String(rawDate);

  if (/^\d{4}-\d{2}-\d{2}/.test(dateText)) {
    const [year, month, day] = dateText.slice(0, 10).split("-").map(Number);
    const parsedDate = new Date(year, month - 1, day);

    if (rawTime && typeof rawTime === "string" && rawTime.includes(":")) {
      const [hours, minutes] = rawTime.split(":").map(Number);
      parsedDate.setHours(hours || 0, minutes || 0, 0, 0);
    } else {
      parsedDate.setHours(0, 0, 0, 0);
    }

    return parsedDate.getTime();
  }

  const time = new Date(dateText).getTime();

  return Number.isFinite(time) ? time : 0;
};

const formatDateLabel = (rawDate: any) => {
  const timestamp = parseDateTime(rawDate);

  if (!timestamp) return "";

  return new Date(timestamp).toLocaleDateString(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatTimeLabel = (rawTime?: string) => {
  if (!rawTime) return "";

  const [hours, minutes] = rawTime.split(":").map(Number);

  if (!Number.isFinite(hours)) return "";

  const date = new Date();
  date.setHours(hours || 0, minutes || 0, 0, 0);

  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTripDepartLabel = (item: any) => {
  const departDate = formatDateLabel(
    item?.departDate ||
      item?.date ||
      item?.tripDate ||
      item?.departureDate ||
      item?.startDate ||
      item?.travelDate ||
      item?.scheduledDate,
  );

  const departTime = formatTimeLabel(item?.departTime);

  if (!departDate && !departTime) {
    return "DEPART: Not set";
  }

  return `DEPART: ${departDate || ""}${departTime ? ` • ${departTime}` : ""}`;
};

const formatTripArrivalLabel = (item: any) => {
  const arrivalDate = formatDateLabel(item?.arriveDate || item?.arrivalDate);
  const arriveTime = formatTimeLabel(item?.arriveTime);

  if (!arrivalDate && !arriveTime) {
    return "";
  }

  return `ARRIVAL: ${arrivalDate || ""}${arriveTime ? ` • ${arriveTime}` : ""}`;
};

const ownerMatches = (item: any, ownerId: string) => {
  return (
    item?.userId === ownerId ||
    item?.ownerId === ownerId ||
    item?.ownerUserId === ownerId ||
    item?.sellerUserId === ownerId ||
    item?.businessId === ownerId ||
    item?.createdBy === ownerId ||
    item?.sellerId === ownerId ||
    item?.providerId === ownerId ||
    item?.hostId === ownerId ||
    item?.driverId === ownerId ||
    item?.user?.id === ownerId ||
    item?.user?._id === ownerId ||
    item?.user?.uid === ownerId ||
    item?.driver?.id === ownerId ||
    item?.driver?._id === ownerId ||
    item?.driver?.uid === ownerId
  );
};

const resolveId = (item: any) => item?.id || item?._id;

const isTripType = (type?: ListingType) => type === "hikes";

const resolveTitle = (item: any) => {
  const fromText = valueToDisplayText(item?.from);
  const toText = valueToDisplayText(item?.to);

  if (fromText && toText) {
    return `${fromText} to ${toText}`;
  }

  return (
    valueToDisplayText(item?.title) ||
    valueToDisplayText(item?.name) ||
    valueToDisplayText(item?.productName) ||
    valueToDisplayText(item?.accommodationName) ||
    valueToDisplayText(item?.tripName) ||
    valueToDisplayText(item?.carName) ||
    "Untitled"
  );
};

const resolveDescription = (item: any, type?: ListingType) => {
  if (isTripType(type)) {
    const arrivalLabel = formatTripArrivalLabel(item);
    const carType = valueToDisplayText(item?.carType);
    const seatsNumber = Number(
      item?.seatsAvailable ?? item?.availableSeats ?? item?.seats ?? 0,
    );
    const seats =
      seatsNumber > 0
        ? `${seatsNumber} seat${seatsNumber === 1 ? "" : "s"} left`
        : "";

    return [arrivalLabel, carType, seats].filter(Boolean).join(" • ");
  }

  return (
    valueToDisplayText(item?.description) ||
    valueToDisplayText(item?.summary) ||
    valueToDisplayText(item?.details) ||
    ""
  );
};

const resolveImage = (item: any, type?: ListingType) => {
  const image =
    valueToDisplayText(item?.imageUrl) ||
    valueToDisplayText(item?.image) ||
    valueToDisplayText(item?.mainImage) ||
    valueToDisplayText(item?.coverImage) ||
    valueToDisplayText(item?.images?.[0]) ||
    valueToDisplayText(item?.photos?.[0]) ||
    valueToDisplayText(item?.gallery?.[0]) ||
    "";

  if (!image && isTripType(type)) {
    return TRIP_PLACEHOLDER_IMAGE;
  }

  return image;
};

const resolveLocation = (item: any) => {
  const fromText = valueToDisplayText(item?.from);
  const toText = valueToDisplayText(item?.to);

  if (fromText && toText) {
    return `${fromText} → ${toText}`;
  }

  return (
    valueToDisplayText(item?.location) ||
    valueToDisplayText(item?.pickupLocation) ||
    valueToDisplayText(item?.address) ||
    valueToDisplayText(item?.city) ||
    fromText ||
    toText ||
    ""
  );
};

const resolvePrice = (item: any) => {
  const value =
    item?.price ??
    item?.priceMk ??
    item?.priceMWK ??
    item?.amount ??
    item?.cost ??
    item?.rate ??
    item?.pricePerNight ??
    item?.pricePerDay ??
    item?.dailyRate ??
    item?.seatPrice ??
    item?.fare ??
    "";

  return value;
};

const resolveNumericPrice = (item: any) => {
  const rawPrice = resolvePrice(item);

  if (typeof rawPrice === "number") return rawPrice;

  const parsedPrice = Number(String(rawPrice).replace(/[^\d.]/g, ""));

  return Number.isFinite(parsedPrice) ? parsedPrice : 0;
};

const resolveCategory = (item: any, type?: ListingType) => {
  if (isTripType(type)) {
    return valueToDisplayText(item?.carType) || "Trip";
  }

  if (type === "carHire") {
    return (
      valueToDisplayText(item?.transmission) ||
      valueToDisplayText(item?.vehicleType) ||
      valueToDisplayText(item?.carType) ||
      "Car hire"
    );
  }

  return (
    valueToDisplayText(item?.category) ||
    valueToDisplayText(item?.carType) ||
    valueToDisplayText(item?.roomType) ||
    valueToDisplayText(item?.type) ||
    valueToDisplayText(item?.vehicleType) ||
    "Other"
  );
};

const resolveCarTitle = (item: any) => {
  const title = `${item?.brand ? `${item.brand} ` : ""}${item?.make || ""} ${
    item?.model || ""
  }`.trim();

  return title || resolveTitle(item) || "Car Hire";
};

const resolveCarSubtitle = (item: any) =>
  `${valueToDisplayText(item?.pickupLocation) || "-"} • ${
    valueToDisplayText(item?.transmission) || "-"
  } • ${item?.seats || "-"} seats`;

const resolveCarDescription = (item: any) =>
  valueToDisplayText(item?.hiredPeriodMessage) ||
  valueToDisplayText(item?.description) ||
  `${item?.year || "Available"} ${item?.make || "car"} for hire.`;

const resolveCarImage = (item: any) => {
  const normalizedImages = normalizeCarImages(item?.images, item?.imageUrl);

  return normalizedImages[0] || resolveImage(item, "carHire");
};

const resolveCarPrice = (item: any) => {
  const pricePerDay = Number(item?.pricePerDay || 0);

  return pricePerDay > 0
    ? `MWK ${pricePerDay.toLocaleString()} / day`
    : "Price on request";
};

const resolveCarLocation = (item: any) =>
  valueToDisplayText(item?.pickupLocation) || resolveLocation(item);

const resolveCarCategory = (item: any) =>
  valueToDisplayText(item?.transmission) || "Car hire";

const resolveCarBadgeLabel = (item: any) =>
  item?.availability === false || item?.isMaintenance === true
    ? "Unavailable"
    : "Available";

const resolveDiscount = (item: any) => {
  const rawDiscount =
    item?.discountPercentage ??
    item?.discountPercent ??
    item?.discount ??
    item?.percentageOff ??
    0;

  const discount = Number(rawDiscount);

  return Number.isFinite(discount) ? discount : 0;
};

const resolveCreatedAt = (item: any) => {
  const rawDate =
    item?.createdAt ||
    item?.updatedAt ||
    item?.dateCreated ||
    item?.created_at ||
    "";

  const time = new Date(rawDate).getTime();

  return Number.isFinite(time) ? time : 0;
};

const resolveTripDateTime = (item: any) => {
  const rawDate =
    item?.departDate ||
    item?.date ||
    item?.tripDate ||
    item?.departureDate ||
    item?.startDate ||
    item?.travelDate ||
    item?.scheduledDate ||
    "";

  return parseDateTime(rawDate, item?.departTime);
};

const isFutureTrip = (item: any) => {
  const tripTime = resolveTripDateTime(item);

  if (!tripTime) return false;

  return tripTime >= getTodayStartTime();
};

const normaliseText = (value: unknown) =>
  valueToDisplayText(value).toLowerCase().trim();

const itemMatchesSearch = (item: any, search: string, type?: ListingType) => {
  const query = normaliseText(search);

  if (!query) return true;

  const searchableText = [
    resolveTitle(item),
    formatTripDepartLabel(item),
    resolveDescription(item, type),
    resolveCategory(item, type),
    resolveLocation(item),
    item?.brand,
    item?.make,
    item?.model,
    item?.pickupLocation,
    item?.transmission,
    item?.seats,
    item?.businessName,
    item?.from,
    item?.to,
    item?.departDate,
    item?.arriveDate,
    item?.carType,
  ]
    .map(normaliseText)
    .join(" ");

  return searchableText.includes(query);
};

const itemMatchesLocation = (item: any, location: string) => {
  const query = normaliseText(location);

  if (!query) return true;

  const locationText = [
    resolveLocation(item),
    item?.address,
    item?.city,
    item?.area,
    item?.from,
    item?.to,
    item?.pickupLocation,
    item?.dropoffLocation,
  ]
    .map(normaliseText)
    .join(" ");

  return locationText.includes(query);
};

const getPriceBounds = (items: any[]): [number, number] => {
  const prices = items
    .map(resolveNumericPrice)
    .filter((price) => Number.isFinite(price) && price > 0);

  if (!prices.length) return [0, 0];

  return [Math.min(...prices), Math.max(...prices)];
};

const getDefaultFiltersForItems = (items: any[]): BusinessListingFilters => {
  const [minPrice, maxPrice] = getPriceBounds(items);

  return {
    ...DEFAULT_FILTERS,
    priceRange: [minPrice, maxPrice],
  };
};

const getCategoryOptions = (
  items: any[],
  type?: ListingType,
): BusinessListingFilterOption[] => {
  const categoryCounts = items.reduce<Record<string, number>>((acc, item) => {
    const category = resolveCategory(item, type);

    acc[category] = (acc[category] || 0) + 1;

    return acc;
  }, {});

  return Object.entries(categoryCounts)
    .map(([category, count]) => ({
      value: category,
      label: category,
      count,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const isDeliveryEligible = (item: any, type?: ListingType) => {
  if (type !== "products") return true;

  return Boolean(
    item?.eligibleForDelivery ||
    item?.deliveryEligible ||
    item?.canDeliver ||
    item?.supportsDelivery ||
    item?.deliveryAvailable ||
    item?.delivery?.eligible ||
    item?.delivery?.available,
  );
};

const isAvailable = (item: any, type?: ListingType) => {
  if (isTripType(type)) {
    return (
      Number(item?.seatsAvailable ?? item?.availableSeats ?? item?.seats ?? 0) >
      0
    );
  }

  if (type === "carHire") {
    if (item?.isMaintenance === true) return false;
    if (typeof item?.availability === "boolean") return item.availability;
    if (typeof item?.available === "boolean") return item.available;
    if (typeof item?.isAvailable === "boolean") return item.isAvailable;

    return true;
  }

  if (type === "accommodations") {
    if (typeof item?.available === "boolean") return item.available;
    if (typeof item?.isAvailable === "boolean") return item.isAvailable;
    if (typeof item?.roomsAvailable === "number")
      return item.roomsAvailable > 0;

    return true;
  }

  if (typeof item?.available === "boolean") return item.available;
  if (typeof item?.isAvailable === "boolean") return item.isAvailable;
  if (typeof item?.inStock === "boolean") return item.inStock;

  if (typeof item?.stock === "number") return item.stock > 0;
  if (typeof item?.quantity === "number") return item.quantity > 0;

  return true;
};

const applyListingFilters = (
  items: any[],
  filters: BusinessListingFilters,
  type?: ListingType,
): any[] => {
  const filteredItems = items.filter((item) => {
    const price = resolveNumericPrice(item);
    const category = resolveCategory(item, type);
    const discount = resolveDiscount(item);

    const matchesSearch = itemMatchesSearch(item, filters.search, type);
    const matchesLocation = itemMatchesLocation(item, filters.location);
    const matchesCategory =
      filters.categories.length === 0 || filters.categories.includes(category);
    const matchesPrice =
      price >= filters.priceRange[0] && price <= filters.priceRange[1];
    const matchesDiscount =
      isTripType(type) || type === "carHire"
        ? true
        : discount >= filters.minDiscount;
    const matchesDelivery =
      type === "products"
        ? !filters.deliveryEligibleOnly || isDeliveryEligible(item, type)
        : true;
    const matchesAvailability =
      !filters.availableOnly || isAvailable(item, type);

    return (
      matchesSearch &&
      matchesLocation &&
      matchesCategory &&
      matchesPrice &&
      matchesDiscount &&
      matchesDelivery &&
      matchesAvailability
    );
  });

  return [...filteredItems].sort((firstItem, secondItem) => {
    if (filters.sortBy === "priceLowHigh") {
      return resolveNumericPrice(firstItem) - resolveNumericPrice(secondItem);
    }

    if (filters.sortBy === "priceHighLow") {
      return resolveNumericPrice(secondItem) - resolveNumericPrice(firstItem);
    }

    if (filters.sortBy === "discountHighLow") {
      return resolveDiscount(secondItem) - resolveDiscount(firstItem);
    }

    if (filters.sortBy === "newest") {
      if (isTripType(type)) {
        return resolveTripDateTime(firstItem) - resolveTripDateTime(secondItem);
      }

      return resolveCreatedAt(secondItem) - resolveCreatedAt(firstItem);
    }

    if (isTripType(type)) {
      return resolveTripDateTime(firstItem) - resolveTripDateTime(secondItem);
    }

    return 0;
  });
};

const getPageNumbers = (currentPage: number, totalPages: number) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));

  return Array.from({ length: 5 }, (_, index) => start + index);
};

const ListingGrid = ({
  items,
  type,
  onAddProductToCart,
}: {
  items: any[];
  type: ListingType;
  onAddProductToCart: (item: any) => void;
}) => {
  const navigate = useNavigate();

  if (!items.length) {
    return null;
  }

  return (
    <Grid
      templateColumns={{
        base: "repeat(2, 1fr)",
        lg: "repeat(3, 1fr)",
        xl: "repeat(4, 1fr)",
      }}
      gap={{ base: 4, md: 5 }}
    >
      {items.map((item) => {
        const itemId = resolveId(item);

        const actionLabel =
          type === "products"
            ? "Add"
            : type === "accommodations"
              ? "Book"
              : type === "carHire"
                ? "Hire"
                : "Book";

        const route =
          type === "products"
            ? `/product/${itemId}`
            : type === "accommodations"
              ? `/search-details/accommodations/${itemId}`
              : type === "carHire"
                ? `/search-details/cars/${itemId}`
                : `/hike/checkout/${itemId}`;

        const badgeLabel =
          type === "products"
            ? "Product"
            : type === "accommodations"
              ? "Stay"
              : type === "carHire"
                ? resolveCarBadgeLabel(item)
                : "Trip";

        const cardTitle =
          type === "carHire" ? resolveCarTitle(item) : resolveTitle(item);

        const cardSubtitle =
          type === "carHire"
            ? resolveCarSubtitle(item)
            : isTripType(type)
              ? formatTripDepartLabel(item)
              : resolveCategory(item, type);

        const cardDescription =
          type === "carHire"
            ? resolveCarDescription(item)
            : resolveDescription(item, type);

        const cardImage =
          type === "carHire" ? resolveCarImage(item) : resolveImage(item, type);

        const cardPrice =
          type === "carHire" ? resolveCarPrice(item) : resolvePrice(item);

        const cardLocation =
          type === "carHire" ? resolveCarLocation(item) : resolveLocation(item);

        const cardCategory =
          type === "carHire"
            ? resolveCarCategory(item)
            : resolveCategory(item, type);

        return (
          <GridItem key={itemId}>
            <BusinessListingShowcaseCard
              title={cardTitle}
              subtitle={cardSubtitle}
              description={cardDescription}
              image={cardImage}
              price={cardPrice}
              location={cardLocation}
              category={cardCategory}
              badgeLabel={badgeLabel}
              actionLabel={actionLabel}
              onCardClick={() => navigate(route)}
              onActionClick={() => {
                if (type === "products") {
                  onAddProductToCart(item);
                  return;
                }

                navigate(route);
              }}
            />
          </GridItem>
        );
      })}
    </Grid>
  );
};

const BusinessPageHeader = ({ profile }: { profile: ProfileData }) => {
  const { theme } = useCustomTheme();

  const businessName = profile.businessName || profile.name || "Business";

  const aboutText = profile.description || profile.bio || "";

  const locationText = Array.from(
    new Set(
      [profile.businessAddress, profile.city, profile.country].filter(Boolean),
    ),
  ).join(" • ");

  const businessTypeLabel =
    profile.businessType ||
    (profile.role && profile.role !== "guest" ? profile.role : "");

  return (
    <Flex
      align={{ base: "flex-start", lg: "center" }}
      justify="space-between"
      direction={{ base: "column", lg: "row" }}
      gap={5}
      mb={6}
      bg={theme.surface}
      border={`1px solid ${theme.border}`}
      borderRadius={cardsBorderRadii}
      px={{ base: 4, md: 5 }}
      py={{ base: 4, md: 5 }}
      boxShadow={theme.shadowSm}
    >
      <Box flex="1" minW={0} maxW={{ base: "100%", lg: "54%" }}>
        <Heading
          size={{ base: "md", md: "lg" }}
          color={theme.text}
          lineHeight="1.15"
          mb={aboutText ? 2 : 0}
        >
          {businessName} listings
        </Heading>

        {!!aboutText && (
          <Text
            color={theme.textMuted}
            fontSize={{ base: "sm", md: "md" }}
            lineHeight="1.65"
            noOfLines={{ base: 3, md: 4 }}
          >
            {aboutText}
          </Text>
        )}
      </Box>

      <Flex
        align={{ base: "flex-start", sm: "center" }}
        direction={{ base: "column", sm: "row" }}
        gap={3}
        bg={theme.background}
        border={`1px solid ${theme.border}`}
        borderRadius={cardsBorderRadii}
        px={{ base: 3, md: 4 }}
        py={3}
        w={{ base: "100%", lg: "auto" }}
        minW={{ base: "100%", lg: "520px" }}
        maxW={{ base: "100%", lg: "760px" }}
      >
        <Flex align="center" gap={3} flex="1" minW={0} w="100%">
          <Avatar
            src={profile.logoURL}
            name={businessName}
            boxSize={{ base: "46px", md: "52px" }}
            border="2px solid"
            borderColor={theme.borderStrong}
            bg={theme.surfaceAlt}
            boxShadow={theme.shadowSm}
            flexShrink={0}
          />

          <Box minW={0} flex="1">
            <Text
              color={theme.text}
              fontWeight="900"
              fontSize={{ base: "sm", md: "md" }}
              noOfLines={1}
            >
              {businessName}
            </Text>

            {!!locationText && (
              <Text
                color={theme.textMuted}
                fontSize="xs"
                fontWeight="700"
                noOfLines={2}
              >
                {locationText}
              </Text>
            )}
          </Box>
          <HStack spacing={2} mt={2} flexWrap="wrap">
            {!!profile.businessCode && profile.businessQrGenerated && (
              <Badge
                borderRadius={cardsBorderRadii}
                px={2.5}
                py={1.5}
                colorScheme="green"
                textTransform="none"
              >
                {profile.businessCode}
              </Badge>
            )}
          </HStack>
        </Flex>

        <BusinessQrShareActions
          businessUid={profile.id}
          businessName={businessName}
        />
      </Flex>
    </Flex>
  );
};

const EmptyFilteredState = ({
  onResetFilters,
}: {
  onResetFilters: () => void;
}) => {
  const { theme } = useCustomTheme();

  return (
    <Box
      bg={theme.surface}
      border={`1px solid ${theme.border}`}
      borderRadius={cardsBorderRadii}
      p={{ base: 5, md: 8 }}
      textAlign="center"
    >
      <Flex
        boxSize="52px"
        mx="auto"
        mb={4}
        borderRadius={cardsBorderRadii}
        align="center"
        justify="center"
        bg={theme.primarySoft}
        color={theme.primary}
      >
        <Icon as={SlidersIcon} boxSize={5} />
      </Flex>

      <Heading size="sm" color={theme.text} mb={2}>
        No items match these filters
      </Heading>

      <Text color={theme.textMuted} mb={5}>
        Try changing the price range, category, availability, location, or
        search term.
      </Text>

      <Button
        onClick={onResetFilters}
        borderRadius={cardsBorderRadii}
        color={theme.textOnPrimary}
        bgImage={theme.gradients.primaryButton}
        _hover={{
          bgImage: theme.gradients.primaryButtonHover,
        }}
      >
        Reset filters
      </Button>
    </Box>
  );
};

const ListingsPagination = ({
  currentPage,
  totalPages,
  visibleCount,
  totalCount,
  canViewMore,
  onPageChange,
  onViewMore,
}: {
  currentPage: number;
  totalPages: number;
  visibleCount: number;
  totalCount: number;
  canViewMore: boolean;
  onPageChange: (page: number) => void;
  onViewMore: () => void;
}) => {
  const { theme } = useCustomTheme();

  if (totalCount <= ITEMS_PER_PAGE) {
    return null;
  }

  const pageNumbers = getPageNumbers(currentPage, totalPages);

  return (
    <Flex
      mt={6}
      gap={4}
      align={{ base: "stretch", md: "center" }}
      justify="space-between"
      direction={{ base: "column", lg: "row" }}
      bg={theme.surface}
      border={`1px solid ${theme.border}`}
      borderRadius={cardsBorderRadii}
      p={{ base: 4, md: 5 }}
    >
      <Box>
        <Text color={theme.text} fontWeight="900" fontSize="sm">
          Showing {visibleCount} of {totalCount}
        </Text>

        <Text color={theme.textMuted} fontSize="xs" fontWeight="700">
          Page {currentPage} of {totalPages}. Showing {ITEMS_PER_PAGE} items per
          page.
        </Text>
      </Box>

      <Flex
        gap={3}
        align={{ base: "stretch", sm: "center" }}
        justify={{ base: "stretch", lg: "flex-end" }}
        direction={{ base: "column", sm: "row" }}
        flexWrap="wrap"
      >
        {canViewMore && (
          <Button
            onClick={onViewMore}
            borderRadius={cardsBorderRadii}
            color={theme.textOnPrimary}
            bgImage={theme.gradients.primaryButton}
            _hover={{
              bgImage: theme.gradients.primaryButtonHover,
            }}
          >
            View more
          </Button>
        )}

        <ButtonGroup
          isAttached
          variant="outline"
          display="flex"
          flexWrap="wrap"
          justifyContent={{ base: "center", sm: "flex-start" }}
        >
          <IconButton
            aria-label="Previous page"
            icon={<Icon as={ChevronLeftIcon} />}
            onClick={() => onPageChange(currentPage - 1)}
            isDisabled={currentPage <= 1}
            borderColor={theme.border}
            color={theme.text}
          />

          {pageNumbers.map((page) => (
            <Button
              key={page}
              onClick={() => onPageChange(page)}
              bg={page === currentPage ? theme.primarySoft : theme.surface}
              color={page === currentPage ? theme.primary : theme.text}
              borderColor={page === currentPage ? theme.primary : theme.border}
              fontWeight="900"
              minW="42px"
            >
              {page}
            </Button>
          ))}

          <IconButton
            aria-label="Next page"
            icon={<Icon as={ChevronRightIcon} />}
            onClick={() => onPageChange(currentPage + 1)}
            isDisabled={currentPage >= totalPages}
            borderColor={theme.border}
            color={theme.text}
          />
        </ButtonGroup>
      </Flex>
    </Flex>
  );
};

const BusinessDetailsPage: React.FC = () => {
  const { id = "" } = useParams();
  const { theme } = useCustomTheme();
  const toast = useToast();
  const filterDrawer = useDisclosure();
  const dispatch = useAppDispatch();

  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [filtersByTab, setFiltersByTab] = useState<FiltersByBusinessTab>({});
  const [visibleLimit, setVisibleLimit] = useState(ITEMS_PER_PAGE);

  const combined = useCombinedStore() as any;
  const { list: hikeTrips = [] } = useAppSelector((state) => state.hikeTrips);

  const products = combined?.products || [];
  const accommodations = combined?.accommodations || [];
  const carHire = combined?.carHire || combined?.cars || [];

  useEffect(() => {
    dispatch(fetchAllTrips());
  }, [dispatch]);

  useEffect(() => {
    let mounted = true;

    const loadProfileFromFirestore = async () => {
      const snap = await getDoc(doc(db, "users", id));

      if (!snap.exists()) {
        return null;
      }

      return mapProfileData(snap.id, snap.data());
    };

    const fetchProfile = async () => {
      setLoading(true);

      try {
        let resolvedProfile: ProfileData | null = null;

        /*
         * Use the public backend first. This keeps the page publicly readable,
         * uses the same profile shape as the public-business API, and includes
         * the QR-generation status and stored WhatsApp business code.
         */
        try {
          const response = await fetch(
            `${API_BASE}/api/v1/public/businesses/${encodeURIComponent(id)}`,
            {
              method: "GET",
              headers: {
                Accept: "application/json",
              },
            },
          );

          if (response.ok) {
            const payload = await response.json();
            const publicProfile = payload?.data || payload;

            if (publicProfile) {
              resolvedProfile = mapProfileData(id, publicProfile);
            }
          }
        } catch (publicProfileError) {
          console.error(
            "Failed to load the public business profile:",
            publicProfileError,
          );
        }

        /*
         * Keep the existing Firestore behaviour as a fallback so this page
         * remains backward compatible during deployment.
         */
        if (!resolvedProfile) {
          resolvedProfile = await loadProfileFromFirestore();
        }

        if (!mounted) return;

        setProfile(resolvedProfile);
      } catch (error) {
        console.error("Failed to load business profile:", error);

        if (mounted) {
          setProfile(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    if (id) {
      void fetchProfile();
    } else {
      setProfile(null);
      setLoading(false);
    }

    return () => {
      mounted = false;
    };
  }, [id]);

  const ownedProducts = useMemo(
    () => products.filter((item: any) => ownerMatches(item, id)),
    [products, id],
  );

  const ownedTrips = useMemo(
    () =>
      hikeTrips
        .filter(isFutureTrip)
        .filter((trip: any) => ownerMatches(trip, id)),
    [hikeTrips, id],
  );

  const ownedAccommodations = useMemo(
    () => accommodations.filter((item: any) => ownerMatches(item, id)),
    [accommodations, id],
  );

  const ownedCars = useMemo(
    () => carHire.filter((item: any) => ownerMatches(item, id)),
    [carHire, id],
  );

  const businessTabs = useMemo<BusinessTab[]>(() => {
    const tabs: BusinessTab[] = [];

    if (ownedProducts.length) {
      tabs.push({
        key: "products",
        label: `Products (${ownedProducts.length})`,
        type: "products",
        items: ownedProducts,
      });
    }

    if (ownedAccommodations.length) {
      tabs.push({
        key: "accommodations",
        label: `Accommodations (${ownedAccommodations.length})`,
        type: "accommodations",
        items: ownedAccommodations,
      });
    }

    if (ownedCars.length) {
      tabs.push({
        key: "cars",
        label: `Car Hire (${ownedCars.length})`,
        type: "carHire",
        items: ownedCars,
      });
    }

    if (ownedTrips.length) {
      tabs.push({
        key: "trips",
        label: `Trips (${ownedTrips.length})`,
        type: "hikes",
        items: ownedTrips,
      });
    }

    return tabs;
  }, [ownedProducts, ownedAccommodations, ownedCars, ownedTrips]);

  const reusableBusinessTabs = useMemo<ReusableStyledTabItem[]>(
    () =>
      businessTabs.map((tab) => ({
        key: tab.key,
        label: tab.label,
      })),
    [businessTabs],
  );

  const activeTab = businessTabs[activeTabIndex] || businessTabs[0];
  const activeItems = activeTab?.items || [];
  const activeTabKey = activeTab?.key || "products";
  const activeFilters =
    filtersByTab[activeTabKey] || getDefaultFiltersForItems(activeItems);

  const [minPrice, maxPrice] = useMemo(
    () => getPriceBounds(activeItems),
    [activeItems],
  );

  const categoryOptions = useMemo(
    () => getCategoryOptions(activeItems, activeTab?.type),
    [activeItems, activeTab?.type],
  );

  const filteredActiveItems = useMemo(
    () => applyListingFilters(activeItems, activeFilters, activeTab?.type),
    [activeItems, activeFilters, activeTab?.type],
  );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredActiveItems.length / ITEMS_PER_PAGE),
  );

  const currentPage = Math.max(
    1,
    Math.min(Math.ceil(visibleLimit / ITEMS_PER_PAGE), totalPages),
  );

  const paginatedItems = useMemo(
    () => filteredActiveItems.slice(0, visibleLimit),
    [filteredActiveItems, visibleLimit],
  );

  const updateActiveFilters = (nextFilters: BusinessListingFilters) => {
    setFiltersByTab((current) => ({
      ...current,
      [activeTabKey]: nextFilters,
    }));
  };

  useEffect(() => {
    if (!activeTab) return;

    const nextDefaultFilters = getDefaultFiltersForItems(activeItems);

    setFiltersByTab((current) => ({
      ...current,
      [activeTab.key]: nextDefaultFilters,
    }));

    setVisibleLimit(ITEMS_PER_PAGE);
  }, [activeTab?.key, activeItems.length]);

  useEffect(() => {
    setVisibleLimit(ITEMS_PER_PAGE);
  }, [activeFilters]);

  const resetFilters = () => {
    updateActiveFilters(getDefaultFiltersForItems(activeItems));
    setVisibleLimit(ITEMS_PER_PAGE);
  };

  const handleTabChange = (index: number) => {
    setActiveTabIndex(index);
    setVisibleLimit(ITEMS_PER_PAGE);
  };

  const handlePageChange = (page: number) => {
    const safePage = Math.max(1, Math.min(page, totalPages));

    setVisibleLimit(safePage * ITEMS_PER_PAGE);
  };

  const handleViewMore = () => {
    setVisibleLimit((currentLimit) =>
      Math.min(currentLimit + ITEMS_PER_PAGE, filteredActiveItems.length),
    );
  };

  const handleAddProductToCart = (product: any) => {
    dispatch(
      addToCart({
        ...product,
        selectedVariants: [],
        selectedPrice: product?.price || product?.amount || product?.cost || 0,
        selectedImage:
          product?.imageUrl ||
          product?.image ||
          product?.images?.[0] ||
          product?.photos?.[0] ||
          "",
      } as any),
    );

    toast({
      title: "Added to cart",
      description: "Product added to cart",
      status: "success",
      duration: 2200,
      isClosable: true,
    });
  };

  if (loading) {
    return (
      <Box p={6} bg={theme.background} minH="100%">
        <Flex minH="240px" align="center" justify="center">
          <GlobalSpinner />
        </Flex>
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box p={6}>
        <Text>Business not found.</Text>
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }} bg={theme.background} minH="100%">
      <BusinessPageHeader profile={profile} />

      {businessTabs.length > 0 ? (
        <>
          <Box mb={5}>
            <Flex
              align={{ base: "stretch", md: "center" }}
              justify="space-between"
              direction={{ base: "column", md: "row" }}
              gap={3}
            >
              <Box flex="1" minW={0}>
                <ReusableStyledTabs
                  tabs={reusableBusinessTabs}
                  index={activeTabIndex}
                  onChange={handleTabChange}
                  showPanels={false}
                  width="full"
                />
              </Box>

              {activeTab.type !== "hikes" && (
                <Button
                  display={{ base: "inline-flex", lg: "none" }}
                  leftIcon={<Icon as={FilterIcon} />}
                  onClick={filterDrawer.onOpen}
                  borderRadius={cardsBorderRadii}
                  color={theme.textOnPrimary}
                  bgImage={theme.gradients.primaryButton}
                  _hover={{
                    bgImage: theme.gradients.primaryButtonHover,
                  }}
                >
                  Filters
                </Button>
              )}
            </Flex>
          </Box>

          {activeTab.type === "hikes" ? (
            <Box minW={0}>
              <HikeSearchTrips ownerId={id} />
            </Box>
          ) : (
            <>
              <Flex align="flex-start" gap={5}>
                <Box
                  display={{ base: "none", lg: "block" }}
                  w={{ lg: "300px", xl: "330px" }}
                  flexShrink={0}
                  position="sticky"
                  top="90px"
                >
                  <ListingFilterSidebar
                    listingType={activeTab.type}
                    filters={activeFilters}
                    categoryOptions={categoryOptions}
                    minPrice={minPrice}
                    maxPrice={maxPrice}
                    totalItems={activeItems.length}
                    filteredItems={filteredActiveItems.length}
                    onFiltersChange={updateActiveFilters}
                    onResetFilters={resetFilters}
                  />
                </Box>

                <Box flex="1" minW={0}>
                  <Flex
                    align={{ base: "flex-start", md: "center" }}
                    justify="space-between"
                    direction={{ base: "column", md: "row" }}
                    gap={3}
                    mb={4}
                    bg={theme.surface}
                    border={`1px solid ${theme.border}`}
                    borderRadius={cardsBorderRadii}
                    px={{ base: 4, md: 5 }}
                    py={3}
                  >
                    <Box>
                      <Text
                        color={theme.text}
                        fontWeight="900"
                        fontSize={{ base: "sm", md: "md" }}
                      >
                        {filteredActiveItems.length} result
                        {filteredActiveItems.length === 1 ? "" : "s"}
                      </Text>

                      <Text
                        color={theme.textMuted}
                        fontSize="xs"
                        fontWeight="700"
                      >
                        Showing {paginatedItems.length} of{" "}
                        {filteredActiveItems.length}. Filtered from{" "}
                        {activeItems.length} item
                        {activeItems.length === 1 ? "" : "s"}.
                      </Text>
                    </Box>

                    <HStack>
                      <Button
                        display={{ base: "inline-flex", lg: "none" }}
                        leftIcon={<Icon as={SlidersIcon} />}
                        onClick={filterDrawer.onOpen}
                        borderRadius={cardsBorderRadii}
                        variant="outline"
                        borderColor={theme.border}
                        color={theme.text}
                      >
                        Filter
                      </Button>
                    </HStack>
                  </Flex>

                  {paginatedItems.length > 0 ? (
                    <>
                      <ListingGrid
                        items={paginatedItems}
                        type={activeTab.type}
                        onAddProductToCart={handleAddProductToCart}
                      />

                      <ListingsPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        visibleCount={paginatedItems.length}
                        totalCount={filteredActiveItems.length}
                        canViewMore={
                          paginatedItems.length < filteredActiveItems.length
                        }
                        onPageChange={handlePageChange}
                        onViewMore={handleViewMore}
                      />
                    </>
                  ) : (
                    <EmptyFilteredState onResetFilters={resetFilters} />
                  )}
                </Box>
              </Flex>

              <Drawer
                isOpen={filterDrawer.isOpen}
                placement="bottom"
                onClose={filterDrawer.onClose}
                size="full"
              >
                <DrawerOverlay />
                <DrawerContent
                  bg={theme.background}
                  borderTopRadius="28px"
                  maxH="92vh"
                  mt="auto"
                >
                  <DrawerBody p={4} overflowY="auto">
                    <ListingFilterSidebar
                      listingType={activeTab.type}
                      filters={activeFilters}
                      categoryOptions={categoryOptions}
                      minPrice={minPrice}
                      maxPrice={maxPrice}
                      totalItems={activeItems.length}
                      filteredItems={filteredActiveItems.length}
                      onFiltersChange={updateActiveFilters}
                      onResetFilters={resetFilters}
                      onClose={filterDrawer.onClose}
                      showCloseButton
                    />
                  </DrawerBody>
                </DrawerContent>
              </Drawer>
            </>
          )}
        </>
      ) : (
        <Box
          bg={theme.surface}
          border={`1px solid ${theme.border}`}
          borderRadius={cardsBorderRadii}
          p={5}
        >
          <Text color={theme.textMuted}>
            This business has no enlisted items yet.
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default BusinessDetailsPage;
